import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUserGraduate, FaFileAlt, FaEnvelope, FaHandshake,
  FaArrowUp, FaArrowDown, FaFileInvoice, FaChartLine, FaUserPlus,
} from 'react-icons/fa';
import axios from 'axios';
import Navbar  from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';

import { API_URL } from '../config/api';
const SB_OPEN   = 252;
const SB_CLOSED =  74;
const SB_GAP    =  12;
const NB_HEIGHT =  68;
const NB_TOP    =  12;

const TYPE_CONF = {
  inscription: { label: 'Nouvelle inscription',  color: '#198754' },
  devis:       { label: 'Demande de devis',       color: '#fd7e14' },
  newsletter:  { label: 'Abonnement newsletter',  color: '#20c997' },
  partenaire:  { label: 'Demande de partenariat', color: '#6f42c1' },
};

const CHART_CONF = {
  inscriptions: { color: '#198754', label: 'Inscriptions' },
  devis:        { color: '#fd7e14', label: 'Devis'        },
  newsletter:   { color: '#20c997', label: 'Newsletter'   },
  partenaires:  { color: '#6f42c1', label: 'Partenaires'  },
};

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60)    return `${s}s`;
  if (s < 3600)  return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}j`;
};

const initials = (n = '') =>
  n.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

// ── Toast de bienvenue maison ─────────────────────────────────────────────────
const WelcomeToast = ({ user, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Petit délai pour déclencher l'animation d'entrée
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    const t1 = setTimeout(() => setLeaving(true), 5000);
    const t2 = setTimeout(() => onClose(),         5800);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, [onClose]);

  const close = () => { setLeaving(true); setTimeout(onClose, 600); };

  const roleLabel = user?.role === 'super_admin' ? 'Super Administrateur'
                  : user?.role === 'admin'        ? 'Administrateur'
                  : 'Admin';

  const firstName = user?.nom_complet?.split(' ')[0] || user?.email?.split('@')[0] || 'Admin';
  const today     = new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' });

  return (
    <>
      <style>{`
        @keyframes wb-slide-in  { from { opacity:0; transform:translateX(60px) scale(0.92); } to { opacity:1; transform:translateX(0) scale(1); } }
        @keyframes wb-slide-out { from { opacity:1; transform:translateX(0) scale(1); } to { opacity:0; transform:translateX(60px) scale(0.92); } }
        @keyframes wb-progress  { from { width:100%; } to { width:0%; } }
        .wb-wrap { animation: wb-slide-in 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .wb-wrap.leaving { animation: wb-slide-out 0.5s ease forwards; }
        .wb-close-btn:hover { background:rgba(255,255,255,0.14) !important; color:#eef2f7 !important; }
      `}</style>
      <div
        className={`wb-wrap${leaving ? ' leaving' : ''}`}
        style={{
          position: 'fixed',
          top: 90,
          right: 24,
          zIndex: 9999,
          pointerEvents: leaving ? 'none' : 'auto',
        }}
      >
        {/* Carte toast */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: 'linear-gradient(135deg, #0d1f14 0%, #071a10 100%)',
          border: '1px solid rgba(25,135,84,0.45)',
          borderRadius: 18,
          padding: '16px 16px 22px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(25,135,84,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
          minWidth: 300, maxWidth: 360,
          backdropFilter: 'blur(24px)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Halo décoratif */}
          <div style={{
            position: 'absolute', top: -30, right: -30,
            width: 110, height: 110, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(32,201,151,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Avatar initiales */}
          <div style={{
            width: 50, height: 50, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17, fontWeight: 800, color: 'white',
            boxShadow: '0 4px 18px rgba(25,135,84,0.5)',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            letterSpacing: '-0.5px',
          }}>
            {initials(user?.nom_complet || user?.email || '')}
          </div>

          {/* Texte */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <span style={{
                fontSize: 13, fontWeight: 700, color: '#20c997',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                👋 Bienvenue !
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#198754',
                background: 'rgba(25,135,84,0.2)', border: '1px solid rgba(25,135,84,0.4)',
                padding: '2px 8px', borderRadius: 20,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {roleLabel}
              </span>
            </div>
            <p style={{
              margin: 0, fontSize: 16, fontWeight: 700, color: '#eef2f7',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {firstName}
            </p>
            <p style={{
              margin: '3px 0 0', fontSize: 12, color: 'rgba(200,212,224,0.5)',
              fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: 'capitalize',
            }}>
              {today}
            </p>
          </div>

          {/* Bouton fermer */}
          <button className="wb-close-btn" onClick={close} style={{
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 9, width: 28, height: 28, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(200,212,224,0.45)', fontSize: 11, flexShrink: 0,
            transition: 'all 0.2s', alignSelf: 'flex-start',
          }}>✕</button>
        </div>

        {/* Barre de progression */}
        <div style={{
          position: 'absolute', bottom: 0, left: 12, right: 12,
          height: 3, background: 'rgba(255,255,255,0.07)',
          borderRadius: '0 0 8px 8px', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 3,
            background: 'linear-gradient(90deg, #198754, #20c997)',
            animation: `wb-progress 5s linear forwards`,
          }} />
        </div>
      </div>
    </>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Sk = ({ h = 60, r = 12, mb = 0 }) => (
  <div style={{
    height: h, borderRadius: r, marginBottom: mb,
    background: 'linear-gradient(90deg,rgba(128,128,128,0.08) 25%,rgba(128,128,128,0.15) 50%,rgba(128,128,128,0.08) 75%)',
    backgroundSize: '200% 100%', animation: 'db-shimmer 1.4s infinite',
  }} />
);

// ── Dashboard principal ───────────────────────────────────────────────────────
const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [statsData,    setStatsData]    = useState(null);
  const [chartData,    setChartData]    = useState([]);
  const [activites,    setActivites]    = useState([]);
  const [activeTab,    setActiveTab]    = useState('inscriptions');
  const [showToast,    setShowToast]    = useState(false);

  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

  // ── Toast : une seule fois par session de connexion ───────────────────────
  useEffect(() => {
    // On utilise localStorage avec un flag lié au token
    // Il est effacé au logout → réapparaît à chaque nouvelle connexion
    const alreadyShown = localStorage.getItem('welcome_shown');
    if (!alreadyShown) {
      const t = setTimeout(() => {
        setShowToast(true);
        localStorage.setItem('welcome_shown', '1');
      }, 1500); // 1.5s après l'arrivée sur le dashboard
      return () => clearTimeout(t);
    }
  }, []);

  // ── Fetch données dashboard ────────────────────────────────────────────────
  const fetchDashboard = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    axios.get(`${API_URL}/dashboard/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(({ data }) => {
      setStatsData(data.stats);
      setChartData(data.chart);
      setActivites(data.activites);
    })
    .catch(() => setError('Impossible de charger les données du dashboard.'))
    .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // ── Thème couleurs ─────────────────────────────────────────────────────────
  const c = isDark ? {
    bg: '#090c16', card: 'rgba(255,255,255,0.04)', cardB: 'rgba(255,255,255,0.08)',
    text: '#c8d4e0', textDim: '#5a6b82', title: '#eef2f7',
    surface: 'rgba(255,255,255,0.04)', shadow: '0 4px 24px rgba(0,0,0,0.4)',
    grid: 'rgba(255,255,255,0.05)', tabBg: 'rgba(255,255,255,0.06)', tabActive: 'rgba(255,255,255,0.12)',
  } : {
    bg: '#f0f3f8', card: '#ffffff', cardB: 'rgba(0,0,0,0.07)',
    text: '#4d6070', textDim: '#8fa3b5', title: '#1a2535',
    surface: 'rgba(0,0,0,0.04)', shadow: '0 2px 16px rgba(0,0,0,0.07)',
    grid: 'rgba(0,0,0,0.05)', tabBg: 'rgba(0,0,0,0.05)', tabActive: '#ffffff',
  };

  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;

  // ── 4 cartes stats ─────────────────────────────────────────────────────────
  const statCards = statsData ? [
    { id:1, title:'Inscriptions',  value:statsData.inscriptions.total, trend:`${statsData.inscriptions.trend>=0?'+':''}${statsData.inscriptions.trend}%`, trendUp:statsData.inscriptions.up, icon:FaUserGraduate, color:'#198754', bg:'rgba(25,135,84,0.09)',  border:'rgba(25,135,84,0.18)'  },
    { id:2, title:'Devis',         value:statsData.devis.total,        trend:`${statsData.devis.trend>=0?'+':''}${statsData.devis.trend}%`,               trendUp:statsData.devis.up,        icon:FaFileAlt,      color:'#fd7e14', bg:'rgba(253,126,20,0.09)', border:'rgba(253,126,20,0.18)' },
    { id:3, title:'Newsletter',    value:statsData.newsletter.total,   trend:`${statsData.newsletter.trend>=0?'+':''}${statsData.newsletter.trend}%`,     trendUp:statsData.newsletter.up,   icon:FaEnvelope,     color:'#20c997', bg:'rgba(32,201,151,0.09)', border:'rgba(32,201,151,0.18)' },
    { id:4, title:'Partenaires',   value:statsData.partenaires.total,  trend:`${statsData.partenaires.trend>=0?'+':''}${statsData.partenaires.trend}%`,   trendUp:statsData.partenaires.up,  icon:FaHandshake,    color:'#6f42c1', bg:'rgba(111,66,193,0.09)', border:'rgba(111,66,193,0.18)' },
  ] : [];

  // ── Graphique ──────────────────────────────────────────────────────────────
  const chartValues = chartData.map(m => m[activeTab] ?? 0);
  const chartMax    = Math.max(...chartValues, 1);
  const chartConf   = CHART_CONF[activeTab];
  const toY  = (v) => 210 - (v / chartMax) * 185;
  const pts  = chartValues.map((v, i) => `${(i * 800) / Math.max(chartValues.length - 1, 1)},${toY(v)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `${line} L 800,210 L 0,210 Z`;
  const total = chartValues.reduce((a, b) => a + b, 0);
  const fmt   = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n;

  // ── Répartition ────────────────────────────────────────────────────────────
  const repartitionTotal = statsData
    ? (statsData.inscriptions.total + statsData.devis.total + statsData.newsletter.total + statsData.partenaires.total) || 1
    : 1;

  return (
    <div style={{ background: c.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after { box-sizing:border-box }
        @keyframes db-shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        @keyframes db-fade    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

        .db-main    { transition:margin-left 0.28s cubic-bezier(0.4,0,0.2,1);min-height:100vh }
        .db-content { padding:20px 20px 32px;animation:db-fade 0.4s ease }

        .db-stats { display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px }
        .db-stat  { border-radius:14px;padding:20px 18px;border:1px solid;transition:transform 0.22s,box-shadow 0.22s;cursor:default;position:relative;overflow:hidden }
        .db-stat:hover { transform:translateY(-3px) }
        .db-stat-deco  { position:absolute;right:-16px;top:-16px;width:72px;height:72px;border-radius:50%;opacity:0.08 }
        .db-stat-top   { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px }
        .db-stat-icon  { width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:18px;border:1px solid }
        .db-stat-trend { display:flex;align-items:center;gap:4px;font-size:13px;font-weight:600;padding:4px 9px;border-radius:20px }
        .db-stat-label { font-size:14px;font-weight:500;margin-bottom:5px }
        .db-stat-val   { font-size:30px;font-weight:700;letter-spacing:-0.5px;line-height:1 }

        .db-grid  { display:grid;grid-template-columns:1fr 308px;gap:14px }
        .db-left  { display:flex;flex-direction:column;gap:14px }
        .db-right { display:flex;flex-direction:column;gap:14px }
        .db-card  { border-radius:14px;padding:22px;border:1px solid }
        .db-card-head  { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px }
        .db-card-title { font-size:17px;font-weight:700;letter-spacing:-0.1px;margin:0 0 2px }
        .db-card-sub   { font-size:13px;font-weight:400;margin:0 }

        .db-tabs { display:flex;gap:4px;padding:4px;border-radius:9px;flex-wrap:wrap }
        .db-tab  { padding:5px 11px;border:none;border-radius:6px;font-size:12px;font-weight:600;font-family:'Plus Jakarta Sans',sans-serif;cursor:pointer;transition:all 0.2s }

        .db-chart-svg    { width:100%;height:230px }
        .db-chart-labels { display:flex;justify-content:space-between;padding:6px 2px 0 }
        .db-chart-label  { font-size:10.5px;font-weight:500 }
        .db-chart-foot   { display:grid;grid-template-columns:repeat(3,1fr);gap:10px;padding-top:14px;margin-top:14px;border-top:1px solid }
        .db-cstat-label  { font-size:12px;font-weight:500;margin-bottom:3px }
        .db-cstat-val    { font-size:17px;font-weight:700 }

        .db-activities { display:flex;flex-direction:column;gap:8px }
        .db-activity   { display:flex;align-items:center;gap:12px;padding:12px 13px;border-radius:11px;border:1px solid;cursor:default;transition:transform 0.18s }
        .db-activity:hover { transform:translateX(4px) }
        .db-act-av   { width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:white;flex-shrink:0 }
        .db-act-top  { display:flex;justify-content:space-between;align-items:center;margin-bottom:2px }
        .db-act-user { font-size:14.5px;font-weight:600 }
        .db-act-time { font-size:11px;font-weight:500 }

        .db-actions { display:flex;flex-direction:column;gap:7px }
        .db-action  { display:flex;align-items:center;gap:11px;padding:11px 13px;border-radius:10px;border:1px solid;cursor:default;transition:transform 0.18s }
        .db-action:hover { transform:translateX(3px) }
        .db-action-icon  { width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0 }
        .db-action-label { flex:1;font-size:14px;font-weight:500 }
        .db-action-val   { font-size:18px;font-weight:700 }

        .db-perfs { display:flex;flex-direction:column;gap:12px }
        .db-perf  { display:flex;flex-direction:column;gap:5px }
        .db-perf-row  { display:flex;justify-content:space-between;align-items:center }
        .db-perf-left { display:flex;align-items:center;gap:8px;font-size:14px;font-weight:500 }
        .db-perf-dot  { width:8px;height:8px;border-radius:50%;flex-shrink:0 }
        .db-perf-val  { font-size:16px;font-weight:700 }
        .db-perf-bar  { height:4px;border-radius:2px;overflow:hidden }
        .db-perf-fill { height:100%;border-radius:2px;transition:width 1.4s ease }

        .db-goal-svg   { width:110px;height:110px }
        .db-goal-items { display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:4px }
        .db-goal-item  { display:flex;flex-direction:column;align-items:center;gap:2px;padding:10px 8px;border-radius:10px;border:1px solid }
        .db-goal-label { font-size:12px;font-weight:500 }
        .db-goal-val   { font-size:20px;font-weight:700 }

        @media(max-width:1300px){ .db-stats{grid-template-columns:repeat(2,1fr)} }
        @media(max-width:1100px){ .db-grid{grid-template-columns:1fr} .db-right{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr))} }
        @media(max-width:1024px){ .db-main{margin-left:0 !important} }
        @media(max-width:640px) { .db-stats{grid-template-columns:1fr} .db-content{padding:14px} }
      `}</style>

      {/* ── Toast de bienvenue ── */}
      {showToast && (
        <WelcomeToast user={user} onClose={() => setShowToast(false)} />
      )}

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar  sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Dashboard" />

      <main className="db-main" style={{ marginLeft: mainML, paddingTop: mainPT }}>
        <div className="db-content">

          {/* ── 4 CARTES STATS ── */}
          <div className="db-stats">
            {loading
              ? [1,2,3,4].map(i => <Sk key={i} h={115} />)
              : error
                ? <div style={{ gridColumn:'1/-1', textAlign:'center', color:'#dc3545', padding:20 }}>{error}</div>
                : statCards.map(s => {
                    const Icon = s.icon;
                    const TI   = s.trendUp ? FaArrowUp : FaArrowDown;
                    return (
                      <div key={s.id} className="db-stat" style={{ background:c.card, borderColor:s.border, boxShadow:c.shadow }}>
                        <div className="db-stat-deco" style={{ background:s.color }} />
                        <div className="db-stat-top">
                          <div className="db-stat-icon" style={{ background:s.bg, color:s.color, borderColor:s.border }}>
                            <Icon />
                          </div>
                          <div className="db-stat-trend" style={{
                            color:      s.trendUp ? '#198754' : '#dc3545',
                            background: s.trendUp ? 'rgba(25,135,84,0.1)' : 'rgba(220,53,69,0.1)',
                          }}>
                            <TI style={{ fontSize:9 }} />{s.trend}
                          </div>
                        </div>
                        <p className="db-stat-label" style={{ color:c.textDim }}>{s.title}</p>
                        <p className="db-stat-val"   style={{ color:c.title }}>{fmt(s.value)}</p>
                      </div>
                    );
                  })
            }
          </div>

          {/* ── GRID PRINCIPAL ── */}
          <div className="db-grid">
            <div className="db-left">

              {/* Graphique 4 onglets */}
              <div className="db-card" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <div className="db-card-head">
                  <div>
                    <p className="db-card-title" style={{ color:c.title }}>Évolution sur 12 mois</p>
                    <p className="db-card-sub"   style={{ color:c.textDim }}>Activité par module</p>
                  </div>
                  <div className="db-tabs" style={{ background:c.tabBg }}>
                    {Object.entries(CHART_CONF).map(([key, conf]) => (
                      <button key={key} className="db-tab" onClick={() => setActiveTab(key)} style={{
                        background:  activeTab===key ? c.tabActive : 'transparent',
                        color:       activeTab===key ? conf.color  : c.textDim,
                        boxShadow:   activeTab===key && !isDark ? '0 1px 6px rgba(0,0,0,0.08)' : 'none',
                      }}>
                        {conf.label}
                      </button>
                    ))}
                  </div>
                </div>

                {loading ? <Sk h={230} r={10} /> : error ? (
                  <div style={{ height:230, display:'flex', alignItems:'center', justifyContent:'center', color:'#dc3545' }}>{error}</div>
                ) : (
                  <>
                    <svg className="db-chart-svg" viewBox="0 0 800 230" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="db-cgrad" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%"   stopColor={chartConf.color} stopOpacity={isDark?'0.28':'0.15'} />
                          <stop offset="100%" stopColor={chartConf.color} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {[60,120,180].map(y => (
                        <line key={y} x1="0" y1={y} x2="800" y2={y} stroke={c.grid} strokeWidth="1" strokeDasharray="4 4" />
                      ))}
                      <path d={area} fill="url(#db-cgrad)" />
                      <path d={line} fill="none" stroke={chartConf.color} strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ filter:`drop-shadow(0 2px 6px ${chartConf.color}55)` }} />
                      {chartValues.map((v, i) => (
                        <circle key={i}
                          cx={(i*800)/Math.max(chartValues.length-1,1)} cy={toY(v)}
                          r="4" fill={chartConf.color}
                          stroke={isDark?'#090c16':'#f0f3f8'} strokeWidth="2" />
                      ))}
                    </svg>
                    <div className="db-chart-labels">
                      {chartData.map((m, i) => (
                        <span key={i} className="db-chart-label" style={{ color:c.textDim }}>{m.label}</span>
                      ))}
                    </div>
                    <div className="db-chart-foot" style={{ borderTopColor:c.cardB }}>
                      {[
                        { label:'Total',          val:total,                                            color:c.title         },
                        { label:'Moy. mensuelle', val:Math.round(total/Math.max(chartValues.length,1)), color:c.title         },
                        { label:'Meilleur mois',  val:Math.max(...chartValues),                         color:chartConf.color },
                      ].map((s, i) => (
                        <div key={i}>
                          <p className="db-cstat-label" style={{ color:c.textDim }}>{s.label}</p>
                          <p className="db-cstat-val"   style={{ color:s.color }}>{s.val}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Activités récentes */}
              <div className="db-card" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <div className="db-card-head">
                  <div>
                    <p className="db-card-title" style={{ color:c.title }}>Activités Récentes</p>
                    <p className="db-card-sub"   style={{ color:c.textDim }}>Dernières actions</p>
                  </div>
                  <button style={{
                    fontSize:13, fontWeight:600, color:'#fd7e14',
                    background:'rgba(253,126,20,0.1)', border:'1px solid rgba(253,126,20,0.2)',
                    padding:'5px 12px', borderRadius:20, cursor:'pointer',
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                    Voir tout
                  </button>
                </div>
                <div className="db-activities">
                  {loading
                    ? [1,2,3,4].map(i => <Sk key={i} h={60} />)
                    : activites.length === 0
                      ? <p style={{ color:c.textDim, textAlign:'center', padding:'20px 0', fontSize:14 }}>Aucune activité récente</p>
                      : activites.map((a, idx) => {
                          const conf = TYPE_CONF[a.type] || { label:a.type, color:'#8fa3b5' };
                          return (
                            <div key={idx} className="db-activity" style={{ background:c.surface, borderColor:c.cardB }}>
                              <div className="db-act-av" style={{ background:conf.color }}>
                                {initials(a.nom_complet)}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div className="db-act-top">
                                  <span className="db-act-user" style={{ color:c.title }}>{a.nom_complet}</span>
                                  <span className="db-act-time" style={{ color:c.textDim }}>{timeAgo(a.created_at)}</span>
                                </div>
                                <span style={{ fontSize:13, color:conf.color, fontWeight:600 }}>{conf.label}</span>
                              </div>
                            </div>
                          );
                        })
                  }
                </div>
              </div>
            </div>

            {/* ── COLONNE DROITE ── */}
            <div className="db-right">

              {/* Objectif mensuel */}
              <div className="db-card" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <div className="db-card-head" style={{ marginBottom:10 }}>
                  <p className="db-card-title" style={{ fontSize:16, color:c.title }}>Objectif Mensuel</p>
                  <div style={{ width:32, height:32, borderRadius:9, background:'rgba(25,135,84,0.1)', color:'#198754', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>
                    <FaChartLine />
                  </div>
                </div>
                {loading ? <Sk h={160} r={10} /> : (() => {
                  const curr   = statsData?.inscriptions?.mois ?? 0;
                  const target = 30;
                  const pct    = Math.min(Math.round((curr / target) * 100), 100);
                  const circ   = 2 * Math.PI * 46;
                  const offset = circ - (pct / 100) * circ;
                  return (
                    <>
                      <div style={{ display:'flex', justifyContent:'center', margin:'10px 0' }}>
                        <svg className="db-goal-svg" viewBox="0 0 110 110">
                          <defs>
                            <linearGradient id="db-gg" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#198754" />
                              <stop offset="100%" stopColor="#20c997" />
                            </linearGradient>
                          </defs>
                          <circle cx="55" cy="55" r="46" fill="none"
                            stroke={isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)'} strokeWidth="8" />
                          <circle cx="55" cy="55" r="46" fill="none"
                            stroke="url(#db-gg)" strokeWidth="8"
                            strokeDasharray={circ} strokeDashoffset={offset}
                            strokeLinecap="round" transform="rotate(-90 55 55)"
                            style={{ filter:'drop-shadow(0 0 5px rgba(25,135,84,0.4))', transition:'stroke-dashoffset 1s ease' }} />
                          <text x="55" y="50" textAnchor="middle" fontSize="20" fontWeight="700"
                            fill={c.title} fontFamily="'Plus Jakarta Sans',sans-serif">{pct}%</text>
                          <text x="55" y="64" textAnchor="middle" fontSize="9" fontWeight="500"
                            fill={c.textDim} fontFamily="'Plus Jakarta Sans',sans-serif">Complété</text>
                        </svg>
                      </div>
                      <div className="db-goal-items">
                        {[{ l:'Ce mois', v:curr }, { l:'Objectif', v:target }].map((g,i) => (
                          <div key={i} className="db-goal-item" style={{ background:c.surface, borderColor:c.cardB }}>
                            <span className="db-goal-label" style={{ color:c.textDim }}>{g.l}</span>
                            <span className="db-goal-val"   style={{ color:c.title }}>{g.v}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Ce mois-ci */}
              <div className="db-card" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <p className="db-card-title" style={{ fontSize:16, color:c.title, marginBottom:12 }}>Ce mois-ci</p>
                <div className="db-actions">
                  {loading
                    ? [1,2,3,4].map(i => <Sk key={i} h={50} />)
                    : [
                        { Icon:FaUserPlus,  label:'Inscriptions', val:statsData?.inscriptions?.mois ?? 0, color:'#198754' },
                        { Icon:FaFileInvoice,label:'Devis',       val:statsData?.devis?.mois        ?? 0, color:'#fd7e14' },
                        { Icon:FaEnvelope,  label:'Newsletter',   val:statsData?.newsletter?.mois   ?? 0, color:'#20c997' },
                        { Icon:FaHandshake, label:'Partenaires',  val:statsData?.partenaires?.mois  ?? 0, color:'#6f42c1' },
                      ].map(({ Icon, label, val, color }, i) => (
                        <div key={i} className="db-action" style={{ background:c.surface, borderColor:c.cardB }}>
                          <div className="db-action-icon" style={{ background:`${color}15`, color }}><Icon /></div>
                          <span className="db-action-label" style={{ color:c.text }}>{label}</span>
                          <span className="db-action-val"   style={{ color }}>{val}</span>
                        </div>
                      ))
                  }
                </div>
              </div>

              {/* Répartition */}
              <div className="db-card" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <p className="db-card-title" style={{ fontSize:16, color:c.title, marginBottom:14 }}>Répartition</p>
                {loading
                  ? [1,2,3,4].map(i => <Sk key={i} h={28} mb={10} />)
                  : (
                    <div className="db-perfs">
                      {[
                        { label:'Inscriptions', val:statsData?.inscriptions?.total ?? 0, color:'#198754' },
                        { label:'Devis',        val:statsData?.devis?.total        ?? 0, color:'#fd7e14' },
                        { label:'Newsletter',   val:statsData?.newsletter?.total   ?? 0, color:'#20c997' },
                        { label:'Partenaires',  val:statsData?.partenaires?.total  ?? 0, color:'#6f42c1' },
                      ].map((p, i) => (
                        <div key={i} className="db-perf">
                          <div className="db-perf-row">
                            <div className="db-perf-left" style={{ color:c.text }}>
                              <span className="db-perf-dot" style={{ background:p.color }} />
                              {p.label}
                            </div>
                            <span className="db-perf-val" style={{ color:p.color }}>{p.val}</span>
                          </div>
                          <div className="db-perf-bar" style={{ background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)' }}>
                            <div className="db-perf-fill" style={{
                              width: `${Math.round((p.val / repartitionTotal) * 100)}%`,
                              background: p.color,
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }
              </div>

            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;